<template>
  <article
    class="flex flex-col gap-4 rounded-[1.2rem] border border-[#d8e4f0] bg-white p-4 sm:p-5 shadow-[0_4px_16px_rgba(15,23,42,0.03)] transition hover:border-[#bfd0e6] lg:flex-row lg:items-center lg:justify-between"
  >
    <!-- Left: Icon & Month details -->
    <div class="flex items-start sm:items-center gap-3.5 min-w-0">
      <div
        class="flex h-11 w-11 shrink-0 items-center justify-center rounded-[0.9rem] transition"
        :class="isReady ? 'bg-[#eaf3fb] text-[#2f6fab]' : 'bg-[#fff7ed] text-[#f97316]'"
      >
        <FileText class="h-5 w-5" />
      </div>

      <div class="min-w-0">
        <div class="flex items-center gap-2 flex-wrap">
          <span class="text-[0.72rem] font-extrabold uppercase tracking-[0.08em] text-[#748197]">
            Month {{ monthSlot.month_number }}
          </span>
          <span
            v-if="isCustomSlot"
            class="inline-flex rounded-full bg-[#eaf3fb] px-2 py-0.5 text-[0.68rem] font-extrabold uppercase tracking-[0.08em] text-[#2f6fab]"
          >
            Custom
          </span>
        </div>

        <h3 class="mt-0.5 text-[0.98rem] font-extrabold text-[#0f172a] truncate">
          {{ monthSlot.report_month_label }}
        </h3>

        <p class="mt-0.5 text-[0.85rem] text-[#64748b] truncate">
          {{ secondaryMetaText }}
        </p>
      </div>
    </div>

    <!-- Right: Status Badge & Actions -->
    <div class="flex flex-wrap items-center gap-3 justify-between lg:justify-end">
      <!-- Status Badge -->
      <span
        class="inline-flex rounded-full px-2.5 py-1 text-[0.68rem] font-extrabold uppercase tracking-[0.08em]"
        :class="statusBadgeClasses"
      >
        {{ statusMeta.label }}
      </span>

      <!-- Action Buttons -->
      <div class="flex flex-wrap items-center gap-2">
        <!-- Ready / Generated Actions -->
        <template v-if="isReady">
          <button
            v-if="canView"
            type="button"
            class="inline-flex items-center justify-center gap-2 rounded-[0.9rem] border border-[#173b5f] bg-[#173b5f] px-3.5 py-2 text-[0.88rem] font-extrabold text-white transition hover:-translate-y-0.5"
            @click="$emit('view')"
          >
            <Eye class="h-4 w-4" />
            View Report
          </button>

          <button
            v-if="canView && monthSlot.report?.share_url"
            type="button"
            class="inline-flex items-center justify-center gap-1.5 rounded-[0.9rem] border border-[#bbf7d0] bg-[#f0fdf4] px-3.5 py-2 text-[0.88rem] font-extrabold text-[#15803d] transition hover:-translate-y-0.5 hover:bg-[#dcfce7]"
            title="Copy Share Link"
            @click="handleCopyShareLink"
          >
            <Link class="h-4 w-4" />
            Share
          </button>

          <button
            v-if="canUpdate"
            type="button"
            class="inline-flex items-center justify-center gap-2 rounded-[0.9rem] border border-[#d7e2ef] bg-white px-3.5 py-2 text-[0.88rem] font-extrabold text-[#64748b] transition hover:-translate-y-0.5"
            @click="$emit('update-report')"
          >
            <FilePenLine class="h-4 w-4" />
            Update Report
          </button>

          <button
            v-if="canDelete"
            type="button"
            class="inline-flex items-center justify-center gap-1.5 rounded-[0.9rem] border border-[#fee2e2] bg-[#fff5f5] px-3 py-2 text-[0.88rem] font-extrabold text-[#dc2626] transition hover:-translate-y-0.5 hover:bg-[#fee2e2]"
            title="Delete Report"
            @click="$emit('delete-report')"
          >
            <Trash2 class="h-4 w-4" />
            Delete
          </button>
        </template>

        <!-- Pending / Not Generated Actions -->
        <template v-else>
          <button
            v-if="canGenerate"
            type="button"
            class="inline-flex items-center justify-center gap-2 rounded-[0.9rem] border border-[#173b5f] bg-[#173b5f] px-3.5 py-2 text-[0.88rem] font-extrabold text-white transition hover:-translate-y-0.5"
            @click="$emit('generate')"
          >
            <Plus class="h-4 w-4" />
            {{ isDraft ? "Continue Draft" : `Generate Month ${monthSlot.month_number} Report` }}
          </button>

          <button
            v-if="canUpload"
            type="button"
            class="inline-flex items-center justify-center gap-2 rounded-[0.9rem] border border-[#d7e2ef] bg-white px-3.5 py-2 text-[0.88rem] font-extrabold text-[#64748b] transition hover:-translate-y-0.5"
            @click="$emit('upload')"
          >
            <FileUp class="h-4 w-4" />
            Upload Report
          </button>

          <button
            v-if="isDraft && canDelete"
            type="button"
            class="inline-flex items-center justify-center gap-1.5 rounded-[0.9rem] border border-[#fee2e2] bg-[#fff5f5] px-3 py-2 text-[0.88rem] font-extrabold text-[#dc2626] transition hover:-translate-y-0.5 hover:bg-[#fee2e2]"
            title="Delete Draft"
            @click="$emit('delete-report')"
          >
            <Trash2 class="h-4 w-4" />
            Delete
          </button>
        </template>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  Eye,
  FilePenLine,
  FileText,
  FileUp,
  Link,
  Plus,
  Trash2,
} from "lucide-vue-next";
import { useToast } from "vue-toast-notification";
import type { ReportMonthSlot, ReportStatusMeta } from "@/types/reports";

const toast = useToast();

const props = defineProps<{
  monthSlot: ReportMonthSlot;
  canGenerate: boolean;
  canView: boolean;
  canDownload?: boolean;
  canDelete?: boolean;
  canUpload?: boolean;
  canUpdate?: boolean;
  showHtmlActions?: boolean;
  emptyMessage?: string;
  statusMeta: ReportStatusMeta;
  isCustom?: boolean;
}>();

defineEmits<{
  (event: "generate"): void;
  (event: "view"): void;
  (event: "download-pdf"): void;
  (event: "download-html"): void;
  (event: "upload"): void;
  (event: "update-report"): void;
  (event: "delete-report"): void;
}>();

const isReady = computed(() => {
  const status = String(props.monthSlot.report?.status || props.monthSlot.status).toLowerCase();
  return status === "generated" || status === "published";
});

const isDraft = computed(
  () => String(props.monthSlot.report?.status || props.monthSlot.status).toLowerCase() === "draft"
);

const isUploaded = computed(() => props.monthSlot.report?.source_type === "uploaded");

const isCustomSlot = computed(() => {
  return props.isCustom || props.monthSlot.month_number > 3;
});

const statusBadgeClasses = computed(() => {
  const tone = props.statusMeta?.tone;
  if (tone === "green" || isReady.value) return "bg-[#e6faf4] text-[#0d8b68]";
  if (tone === "amber" || isDraft.value) return "bg-[#fff6d8] text-[#8b6b00]";
  if (tone === "rose") return "bg-[#ffe9ec] text-[#be243d]";
  if (tone === "blue") return "bg-[#eaf3fb] text-[#2f6fab]";
  return "bg-[#ffe9ec] text-[#be243d]";
});

const formattedDate = computed(() => {
  const dateStr =
    props.monthSlot.report?.generated_at ||
    props.monthSlot.report?.created_at ||
    props.monthSlot.last_generated_at;
  if (!dateStr) return "";

  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
});

const secondaryMetaText = computed(() => {
  if (props.monthSlot.report) {
    const actionLabel = isDraft.value
      ? "Last saved"
      : isUploaded.value
      ? props.monthSlot.report?.replaced_at
        ? "Updated"
        : "Uploaded"
      : "Last generated";
    return formattedDate.value ? `${actionLabel} ${formattedDate.value}` : "Report available";
  }
  return "No report has been generated for this month yet.";
});

async function handleCopyShareLink() {
  const shareUrl = props.monthSlot.report?.share_url;
  if (!shareUrl) {
    toast.error("No share link available for this report.");
    return;
  }

  let copied = false;
  try {
    if (navigator?.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(shareUrl);
      copied = true;
    }
  } catch {
    copied = false;
  }

  if (!copied) {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = shareUrl;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      textarea.style.left = "-9999px";
      textarea.style.top = "-9999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      copied = document.execCommand("copy");
      document.body.removeChild(textarea);
    } catch {
      copied = false;
    }
  }

  if (copied) {
    toast.success("Share link copied to clipboard!");
  } else {
    prompt("Copy share link:", shareUrl);
  }
}
</script>
